import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { Footer } from './Footer';
import { Header } from './Header';

export interface PublicLayoutProps {
  currentUrl: string;
  children: ReactNode;
}

/**
 * The shell every public page renders inside.
 *
 * **The public site is light** — Paper ground, White panels, hairline borders —
 * with two solid Ink bands: the closing call to action and the footer. The
 * bands scope `data-theme="dark"` to just themselves rather than this shell
 * carrying it for the whole page, so the logo, the buttons and every token
 * inside them re-ink with no band-specific styling. There is deliberately no
 * theme control in the public header to match: see `Header`.
 *
 * Three other things here are not decoration:
 *
 *   - The skip link is the first focusable element on the page. A keyboard user
 *     should not tab through the whole navigation on every page.
 *   - There is exactly one `<main>`, and it carries `id="main"` so the skip
 *     link has a target and assistive technology has a landmark.
 *   - The layout is a column with the footer pushed down by `mt-auto`, so a
 *     short page does not leave the footer floating mid-screen.
 */
export function PublicLayout({ currentUrl, children }: PublicLayoutProps) {
  const { t } = useI18n();

  return (
    <div className="relative flex min-h-dvh flex-col bg-bg text-text">
      <a href="#main" className="hs-skip-link">
        {t('common.actions.skipToContent')}
      </a>

      <Header currentUrl={currentUrl} />

      <main id="main" className="flex-1">
        {children}
      </main>

      <Footer />
    </div>
  );
}
